import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const StatsWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '120px 0',
  background: '#FFFFFF',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,75,135,0) 0%, rgba(0,75,135,0.2) 50%, rgba(0,75,135,0) 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,75,135,0) 0%, rgba(0,75,135,0.2) 50%, rgba(0,75,135,0) 100%)',
  }
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '80px',
  [theme.breakpoints.down('md')]: {
    gap: '60px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '40px',
  }
}));

const StatItem = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  padding: '0 20px',
  transition: 'transform 0.3s ease',
  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    right: '-40px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: '40px',
    background: 'linear-gradient(180deg, rgba(0,75,135,0) 0%, rgba(0,75,135,0.2) 50%, rgba(0,75,135,0) 100%)',
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  '&:hover': {
    transform: 'translateY(-5px)',
  }
}));

const NumberTypography = styled(Typography)(({ theme }) => ({
  fontSize: '4rem',
  fontWeight: 600,
  color: '#004B87',
  lineHeight: 1,
  marginBottom: '12px',
  fontFamily: 'Poppins, sans-serif',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '40px',
    height: '2px',
    background: '#004B87',
    opacity: 0.3,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '3rem',
  }
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#475569',
  fontWeight: 500,
  textAlign: 'center',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
  }
}));

const stats = [
  { number: 20, label: "Ans d'expériences", suffix: '+' },
  { number: 16, label: 'Laboratoires', suffix: '+' },
  { number: 20, label: 'Accréditations', suffix: '+' },
  { number: 60, label: 'Collaborateurs', suffix: '+' },
  { number: 500, label: 'Clients', suffix: '+' },
];

const AnimatedStat = ({ number, label, suffix }) => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  return (
    <StatItem ref={ref}>
      <NumberTypography>
        {inView ? (
          <CountUp
            start={0}
            end={number}
            duration={2.5}
            separator=" "
            suffix={suffix}
            useEasing={true}
          />
        ) : '0+'}
      </NumberTypography>
      <LabelTypography>
        {label}
      </LabelTypography>
    </StatItem>
  );
};

const StatsSection = () => {
  return (
    <StatsWrapper>
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
        }}
      >
        <StatsContainer>
          {stats.map((stat, index) => (
            <AnimatedStat
              key={index}
              number={stat.number}
              label={stat.label}
              suffix={stat.suffix}
            />
          ))}
        </StatsContainer>
      </Container>
    </StatsWrapper>
  );
};

export default StatsSection;
